import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, RefreshCw, TrendingUp, RotateCcw, XCircle, CheckCircle2 } from 'lucide-react';
import api from '../../services/api';
import { cn } from '../../utils/cn';
import { formatCurrency } from '../../utils/currency';
import { useCompanyCurrency } from '../../hooks/useCompanySettings';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';

const GATEWAY_LABELS = {
  razorpay: 'Razorpay', stripe: 'Stripe', cashfree: 'Cashfree', payu: 'PayU', ccavenue: 'CCAvenue', manual: 'Manual',
};

const STATUS_STYLES = {
  success: 'bg-[#E3F3E8] text-[#2E844A]',
  refunded: 'bg-[#FFF4E5] text-[#8C4B02]',
  rejected: 'bg-[#FDE7E9] text-[#BA0517]',
};

export default function FinancePage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gatewayFilter, setGatewayFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const currency = useCompanyCurrency();

  const load = useCallback(() => {
    setLoading(true);
    api.get('/crm/invoices/payment-logs', {
      params: { limit: 100, gateway: gatewayFilter || undefined, status: statusFilter || undefined },
    })
      .then(res => { setLogs(res.data.logs || []); setStats(res.data.stats || null); })
      .finally(() => setLoading(false));
  }, [gatewayFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className={PAGE}>
      <PageHeader title="Payment History" subtitle="Every payment collected against a customer invoice — gateway-confirmed or manually recorded — plus refunds and rejected attempts, in one place." icon={Wallet} />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
          <p className="text-xs text-[#706E6B] flex items-center gap-1.5"><TrendingUp size={12} /> Total collected</p>
          <p className="text-2xl font-bold text-[#2E2E2E]">{formatCurrency(stats?.totalCollected || 0, currency)}</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
          <p className="text-xs text-[#706E6B] flex items-center gap-1.5"><RotateCcw size={12} /> Total refunded</p>
          <p className="text-2xl font-bold text-[#2E2E2E]">{formatCurrency(stats?.totalRefunded || 0, currency)}</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
          <p className="text-xs text-[#706E6B] flex items-center gap-1.5"><CheckCircle2 size={12} /> Successful payments</p>
          <p className="text-2xl font-bold text-[#2E2E2E]">{stats?.successCount || 0}</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
          <p className="text-xs text-[#706E6B] flex items-center gap-1.5"><XCircle size={12} /> Rejected attempts</p>
          <p className="text-2xl font-bold text-[#2E2E2E]">{stats?.rejectedCount || 0}</p>
        </div>
      </div>

      {stats?.byGateway && Object.keys(stats.byGateway).length > 0 && (
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
          <h3 className="text-sm font-semibold text-[#2E2E2E] mb-3">By Collection Method</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E5E5]">
                  <th className="text-left py-2 pr-4 font-semibold text-[#5C5C5C]">Method</th>
                  <th className="text-right py-2 pr-4 font-semibold text-[#5C5C5C]">Collected</th>
                  <th className="text-right py-2 pr-4 font-semibold text-[#5C5C5C]">Refunded</th>
                  <th className="text-right py-2 pr-4 font-semibold text-[#5C5C5C]">Events</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F2F2]">
                {Object.entries(stats.byGateway).map(([gw, s]) => (
                  <tr key={gw}>
                    <td className="py-2 pr-4 font-medium text-[#2E2E2E]">{GATEWAY_LABELS[gw] || gw}</td>
                    <td className="py-2 pr-4 text-right text-[#2E2E2E]">{formatCurrency(s.collected, currency)}</td>
                    <td className="py-2 pr-4 text-right text-[#2E2E2E]">{formatCurrency(s.refunded, currency)}</td>
                    <td className="py-2 pr-4 text-right text-[#5C5C5C]">{s.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#2E2E2E]">Transaction Log</h3>
          <div className="flex items-center gap-2">
            <select value={gatewayFilter} onChange={e => setGatewayFilter(e.target.value)} className="h-9 px-2 border border-[#D8DDE6] rounded text-sm focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]">
              <option value="">All methods</option>
              {Object.entries(GATEWAY_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-9 px-2 border border-[#D8DDE6] rounded text-sm focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]">
              <option value="">All statuses</option>
              <option value="success">Success</option>
              <option value="refunded">Refunded</option>
              <option value="rejected">Rejected</option>
            </select>
            <button onClick={load} disabled={loading} className="h-9 px-3 border border-[#E5E5E5] rounded text-xs font-semibold text-[#5C5C5C] hover:bg-[#F3F3F3] disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap shrink-0">
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        {logs.length === 0 ? (
          <p className="text-sm text-[#706E6B] py-8 text-center">{loading ? 'Loading…' : 'No payment activity yet.'}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E5E5]">
                  <th className="text-left py-2 pr-4 font-semibold text-[#5C5C5C]">Invoice</th>
                  <th className="text-left py-2 pr-4 font-semibold text-[#5C5C5C]">Method</th>
                  <th className="text-right py-2 pr-4 font-semibold text-[#5C5C5C]">Amount</th>
                  <th className="text-left py-2 pr-4 font-semibold text-[#5C5C5C]">Status</th>
                  <th className="text-left py-2 pr-4 font-semibold text-[#5C5C5C]">Reference</th>
                  <th className="text-left py-2 pr-4 font-semibold text-[#5C5C5C]">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F2F2]">
                {logs.map(log => (
                  <tr key={log._id}>
                    <td className="py-2.5 pr-4 font-medium text-[#2E2E2E]">{log.invoiceNumber || '—'}</td>
                    <td className="py-2.5 pr-4 text-[#444444]">{GATEWAY_LABELS[log.gateway] || log.gateway}</td>
                    <td className="py-2.5 pr-4 text-right font-medium text-[#2E2E2E]">{formatCurrency(log.amount, log.currency || currency)}</td>
                    <td className="py-2.5 pr-4">
                      <span className={cn('px-2 py-0.5 rounded-full text-xs font-semibold', STATUS_STYLES[log.status] || 'bg-[#F3F3F3] text-[#5C5C5C]')}>{log.status.toUpperCase()}</span>
                    </td>
                    <td className="py-2.5 pr-4 text-xs text-[#5C5C5C] font-mono">{log.reference || log.failureReason || '—'}</td>
                    <td className="py-2.5 pr-4 text-xs text-[#706E6B]">{new Date(log.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
